import { createApp } from "vue";
import App from "./App.vue";
import router from "./router"; // Import the router
import "./assets/styles/global.css"; // Import global styles here

import "primeicons/primeicons.css";
import "../src/assets/fonts/font.css"; // Import Fonts List
import "primeflex/themes/primeone-light.css";



//Primevue Imports
import AutoComplete from "primevue/autocomplete";
import Avatar from "primevue/avatar";
import Badge from "primevue/badge";
import Breadcrumb from 'primevue/breadcrumb';
import Button from "primevue/button";
import ButtonGroup from "primevue/buttongroup";
import Calendar from "primevue/calendar";
import Card from "primevue/card";
import Chart from "primevue/chart";
import Checkbox from "primevue/checkbox";
import Chip from "primevue/chip";
import ColorPicker from "primevue/colorpicker";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import DatePicker from "primevue/datepicker";
import Dialog from "primevue/dialog";
import Drawer from "primevue/drawer";
import Divider from "primevue/divider";
import Editor from 'primevue/editor';
import Fieldset from "primevue/fieldset";
import FileUpload from "primevue/fileupload";
import FloatLabel from "primevue/floatlabel";
import IconField from "primevue/iconfield";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import InputNumber from "primevue/inputnumber";
import ToggleSwitch from "primevue/toggleswitch";
import Listbox from "primevue/listbox";
import Image from "primevue/image";
import Message from "primevue/message";
import Menu from "primevue/menu";
import MenuBar from "primevue/menubar";
import MeterGroup from "primevue/metergroup";
import MultiSelect from "primevue/multiselect";
import OverlayBadge from "primevue/overlaybadge";
import Popover from "primevue/popover";
import Panel from "primevue/panel";
import PickList from "primevue/picklist";
import ProgressBar from "primevue/progressbar";
import ProgressSpinner from "primevue/progressspinner";
import RadioButton from "primevue/radiobutton";
import Rating from "primevue/rating";
import Select from "primevue/select";
import Slider from "primevue/slider";
import SplitButton from "primevue/splitbutton";
import Tab from "primevue/tab";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import TabView from "primevue/tabview";
import Tag from "primevue/tag";
import TieredMenu from "primevue/tieredmenu";
import Timeline from 'primevue/timeline';
import Textarea from "primevue/textarea";
import Toast from "primevue/toast";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import ConfirmDialog from "primevue/confirmdialog";
import Toolbar from 'primevue/toolbar';
import TreeTable from 'primevue/treetable';
import ContextMenu from 'primevue/contextmenu';
import Tooltip from 'primevue/tooltip';
import Ripple from 'primevue/ripple';


import { createPinia } from "pinia";

//temp storage item for state management
const pinia = createPinia();

// Fix needed for Quill v2: https://github.com/primefaces/primevue/issues/5606#issuecomment-2203975395
Editor.methods.renderValue = function renderValue(value) {
    if (this.quill) {
        if (value) {
            const delta = this.quill.clipboard.convert({ html: value });
            this.quill.setContents(delta, 'silent');
        } else {
            this.quill.setText('');
        }
    }
};

// Anything Prime Vue related
import PrimeVue from "primevue/config";
import Aura from '@primevue/themes/aura';

const app = createApp(App);

app.use(pinia); //Pinia for state storage

// Initialize auth store
import { useAuthStore } from '@/stores/authStore';
const authStore = useAuthStore();
authStore.initializeAuth();

app.use(router); // Use the router

app.use(ToastService); //Toastr Service
app.use(ConfirmationService); //Confirmation Service
app.use(PrimeVue, {
    theme: {
        preset: Aura,
        options: {
            prefix: 'p',
            darkModeSelector: 'system',
            cssLayer: false
        }
    }
});

// Register components globally
app.component("AutoComplete", AutoComplete);
app.component("Avatar", Avatar);
app.component("Badge", Badge);
app.component("Breadcrumb", Breadcrumb);
app.component("Button", Button);
app.component("ButtonGroup", ButtonGroup);
app.component("Calendar", Calendar);
app.component("Card", Card);
app.component("Chart", Chart);
app.component("Chip", Chip);
app.component("ColorPicker", ColorPicker);
app.component("Column", Column);
app.component("Checkbox", Checkbox);
app.component("DataTable", DataTable);
app.component("DatePicker", DatePicker);
app.component("Dialog", Dialog);
app.component("Drawer", Drawer);
app.component("Divider", Divider);
app.component("Dropdown", Select); // PrimeVue v4: Dropdown deprecated, use Select (alias for backward compatibility)
app.component("Editor", Editor);
app.component("Fieldset", Fieldset);
app.component("FileUpload", FileUpload);
app.component("FloatLabel", FloatLabel);
app.component("Image", Image);
app.component("InputText", InputText);
app.component("InputIcon", InputIcon);
app.component("InputGroup", InputGroup);
app.component("InputGroupAddon", InputGroupAddon);
app.component("InputNumber", InputNumber);
// PrimeVue v4: InputSwitch deprecated, use ToggleSwitch (alias for backward compatibility)
app.component("InputSwitch", ToggleSwitch);
app.component("ToggleSwitch", ToggleSwitch);
app.component("IconField", IconField);
app.component("Listbox", Listbox);
app.component("Message", Message);
app.component("Menu", Menu);
app.component("MenuBar", MenuBar);
app.component("MeterGroup", MeterGroup);
app.component("MultiSelect", MultiSelect);
app.component("OverlayBadge", OverlayBadge);
// PrimeVue v4: OverlayPanel deprecated, use Popover (alias for backward compatibility)
app.component("OverlayPanel", Popover);
app.component("Popover", Popover);
app.component("Panel", Panel);
app.component("Picklist", PickList);
app.component("ProgressBar", ProgressBar);
app.component("ProgressSpinner", ProgressSpinner);
app.component("RadioButton", RadioButton);
app.component("Rating", Rating);
app.component("Select", Select);
app.component("Slider", Slider);
app.component("SplitButton", SplitButton);
app.component("Tab", Tab);
app.component("Tabs", Tabs);
app.component("TabList", TabList);
app.component("TabPanel", TabPanel);
app.component("TabPanels", TabPanels);
app.component("TabView", TabView);
app.component("Tag", Tag);
app.component("TextArea", Textarea);
app.component("Textarea", Textarea); // Also register as Textarea for consistency
app.component("TieredMenu", TieredMenu);
app.component('Timeline', Timeline);
app.component("Toast", Toast);
app.component("Toolbar", Toolbar);
app.component("TreeTable", TreeTable);
app.component("ContextMenu", ContextMenu);
app.component("ConfirmDialog", ConfirmDialog);

// Register directives
app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);

app.mount("#app");